/* The console's dialog, from the WebAddBus / WebRemoveBus boards: a scrim, a
   560px card (radius 28, padding 28, gap 18), the condensed title, a round
   close button, s2 fields with the ink + teal-tint focus ring, and the action
   row — quiet Cancel, ink primary (teal until 2026-10-28) — over "Esc to close · Enter to …".
   Rendered inside the .tc tree so the theme variables apply. Every job moved
   out of the classic dashboard (2026-10-28) opens in one of these. */
import React, { useEffect, useId, useRef } from 'react';

const FONT = "'Barlow', Arial, sans-serif";
const COND = "'Barlow Semi Condensed', 'Arial Narrow', sans-serif";

export const ConsoleModal: React.FC<{
  title: string;
  onClose: () => void;
  /** Enter anywhere in the card (outside a textarea) runs this. */
  onSubmit?: () => void;
  children: React.ReactNode;
  /** The action row; [ModalButtons] in most cases. */
  footer?: React.ReactNode;
  /** "Esc to close · Enter to add" — the verb only. */
  enterHint?: string;
  width?: number;
}> = ({ title, onClose, onSubmit, children, footer, enterHint, width = 560 }) => {
  const titleId = useId();
  const card = useRef<HTMLDivElement>(null);
  const back = useRef<Element | null>(null);

  useEffect(() => {
    back.current = document.activeElement;
    const first = card.current?.querySelector<HTMLElement>('input, select, textarea, button:not([data-close])');
    first?.focus();
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    };
    document.addEventListener('keydown', key, true);
    return () => {
      document.removeEventListener('keydown', key, true);
      (back.current as HTMLElement | null)?.focus?.();
    };
  }, [onClose]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div aria-hidden="true" onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(17,21,32,.42)', animation: 'fade 200ms cubic-bezier(.2,0,0,1) both' }} />
      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onSubmit && !(e.target instanceof HTMLTextAreaElement) && !(e.target instanceof HTMLButtonElement)) {
            e.preventDefault();
            onSubmit();
          }
        }}
        style={{
          position: 'relative', width, maxWidth: '100%', maxHeight: 'calc(100vh - 48px)', overflow: 'auto',
          background: 'var(--s1)', color: 'var(--ink)', borderRadius: 28, boxShadow: 'var(--float)', padding: 28,
          boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: 18, fontFamily: FONT,
          animation: 'pop 240ms cubic-bezier(.05,.7,.1,1) both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <h2 id={titleId} style={{ margin: 0, fontFamily: COND, fontSize: 30, lineHeight: '34px', fontWeight: 700 }}>{title}</h2>
          <button type="button" data-close aria-label="Close" onClick={onClose}
            style={{ width: 44, height: 44, margin: '-8px -8px 0 0', flex: 'none', border: 0, borderRadius: '50%', background: 'var(--s2)', color: 'var(--ink)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
          </button>
        </div>
        {children}
        {footer}
        {enterHint && <span style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'right' }}>Esc to close · Enter to {enterHint}</span>}
      </div>
    </div>
  );
};

const btnBase: React.CSSProperties = {
  height: 44, padding: '0 16px', border: 0, borderRadius: 14, fontFamily: FONT, fontSize: 15, fontWeight: 600,
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8, textDecoration: 'none',
};

/** Quiet Cancel and the primary. [danger] makes the primary the red one. */
export const ModalButtons: React.FC<{
  cancel?: string;
  onCancel: () => void;
  primary: string;
  onPrimary: () => void;
  busy?: boolean;
  disabled?: boolean;
  danger?: boolean;
}> = ({ cancel = 'Cancel', onCancel, primary, onPrimary, busy, disabled, danger }) => (
  <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
    <button type="button" onClick={onCancel} style={{ ...btnBase, background: 'var(--s2)', color: 'var(--ink)' }}>{cancel}</button>
    <button
      type="button"
      onClick={onPrimary}
      disabled={busy || disabled}
      style={{
        ...btnBase, padding: '0 22px',
        // Black and white, like the pages' own main buttons (Gabriel, 2026-10-28).
        background: danger ? 'var(--bad)' : 'var(--ink)', color: danger ? '#fff' : 'var(--on-ink)',
        opacity: busy || disabled ? 0.55 : 1, cursor: busy || disabled ? 'default' : 'pointer',
      }}
    >
      {busy && <span className="spin" aria-hidden="true" style={{ width: 14, height: 14 }} />}
      {primary}
    </button>
  </div>
);

/** A labelled field: s2 well, 52 high, radius 14; red ring and line on error. */
export const ModalField: React.FC<{
  label: string;
  error?: string | null;
  hint?: string;
  width?: number | string;
  children: (id: string, focusRing: { onFocus: () => void; onBlur: () => void }) => React.ReactNode;
}> = ({ label, error, hint, width = '100%', children }) => {
  const id = useId();
  const [focus, setFocus] = React.useState(false);
  const ring = error
    ? 'inset 0 0 0 2px var(--bad)'
    : focus ? 'inset 0 0 0 2px var(--ink), 0 0 0 4px var(--teal-tint)' : 'none';
  return (
    <label htmlFor={id} style={{ display: 'flex', flexDirection: 'column', gap: 6, width, minWidth: 0 }}>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{label}</span>
      <span style={{ minHeight: 52, borderRadius: 14, background: 'var(--s2)', padding: '0 16px', display: 'flex', alignItems: 'center', boxShadow: ring }}>
        {children(id, { onFocus: () => setFocus(true), onBlur: () => setFocus(false) })}
      </span>
      {error
        ? <span role="alert" style={{ fontSize: 14, fontWeight: 600, color: 'var(--bad)' }}>{error}</span>
        : hint ? <span style={{ fontSize: 13, color: 'var(--muted)' }}>{hint}</span> : null}
    </label>
  );
};

/** The input inside a [ModalField]. */
export const fieldInput: React.CSSProperties = {
  flex: 1, minWidth: 0, border: 0, background: 'transparent', outline: 'none', fontFamily: FONT, fontSize: 17, color: 'var(--ink)', height: 50,
};

/** A select inside a [ModalField], with the board's chevron. */
export const fieldSelect: React.CSSProperties = {
  ...fieldInput, appearance: 'none', cursor: 'pointer',
  backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23566069' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0 center', paddingRight: 28,
};
